import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { api, ApiError } from '../lib/api';
import { useDebounced } from '../lib/hooks';
import { type I18n, useI18n } from '../lib/i18n';

export const cx = (...c: (string | false | null | undefined)[]): string => c.filter(Boolean).join(' ');

/* ---------- Errors & toasts ---------- */
export function errorText(e: unknown, i18n: I18n): string {
  if (e instanceof ApiError) {
    const key = `error.${e.code}`;
    const vars = e.details && typeof e.details === 'object' ? { ...e.details, price: e.details.priceCents !== undefined ? i18n.money(e.details.priceCents, e.details.currency) : undefined } : {};
    return i18n.has(key) ? i18n.t(key, vars) : e.message;
  }
  if (e instanceof TypeError) return i18n.t('error.network');
  if (e instanceof Error) return e.message;
  return String(e);
}
export const fieldErrors = (e: unknown): Record<string, string> =>
  (e instanceof ApiError && e.details && typeof e.details === 'object' && ['validation_failed', 'missing_placeholders'].includes(e.code) ? e.details : {});

interface ToastItem { id: number; kind: 'ok' | 'error' | 'info'; text: string }
const ToastContext = createContext<(kind: ToastItem['kind'], text: string) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((kind: ToastItem['kind'], text: string) => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x.slice(-3), { id, kind, text }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), kind === 'error' ? 8000 : 4000);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => <div key={t.id} className={`toast toast-${t.kind}`} onClick={() => setItems((x) => x.filter((y) => y.id !== t.id))}>{t.text}</div>)}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const push = useContext(ToastContext);
  const i18n = useI18n();
  return useMemo(() => ({
    ok: (text: string) => push('ok', text),
    info: (text: string) => push('info', text),
    error: (e: unknown) => push('error', errorText(e, i18n)),
  }), [push, i18n]);
}

/* ---------- Buttons ---------- */
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; size?: 'sm' | 'lg'; busy?: boolean };
export function Button({ variant = 'primary', size, busy, className, children, disabled, type = 'button', ...rest }: ButtonProps) {
  return (
    <button type={type} className={cx('btn', `btn-${variant}`, size && `btn-${size}`, className)} disabled={busy || disabled} aria-busy={busy || undefined} {...rest}>
      {busy && <span className="spinner spinner-sm" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function AsyncButton({ onClick, confirm, success, children, ...rest }: Omit<ButtonProps, 'onClick'> & { onClick: () => Promise<unknown>; confirm?: string; success?: string }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  return (
    <Button {...rest} busy={busy} onClick={async () => {
      if (confirm && !window.confirm(confirm)) return;
      setBusy(true);
      try {
        await onClick();
        if (success) toast.ok(success);
      } catch (e) {
        toast.error(e);
      } finally {
        setBusy(false);
      }
    }}>{children}</Button>
  );
}

/* ---------- Form controls ---------- */
export function Field({ label, hint, error, children, className }: { label?: ReactNode; hint?: ReactNode; error?: string | null; children: ReactNode; className?: string }) {
  const i18n = useI18n();
  return (
    <label className={cx('field', error && 'has-error', className)}>
      {label !== undefined && <span className="field-label">{label}</span>}
      {children}
      {error ? <span className="field-error">{i18n.has(`validation.${error}`) ? i18n.t(`validation.${error}`) : error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}
export const Input = (props: InputHTMLAttributes<HTMLInputElement>) => <input className="input" {...props} />;
export const Textarea = (props: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea className="input" rows={4} {...props} />;
export function Select({ options, placeholder, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { options: { value: string; label: ReactNode }[]; placeholder?: string }) {
  return (
    <select className="input" {...rest}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
export function Checkbox({ label, checked, onChange, disabled, hint }: { label: ReactNode; checked: boolean; onChange(v: boolean): void; disabled?: boolean; hint?: ReactNode }) {
  return (
    <label className="checkbox">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}{hint && <small className="muted block">{hint}</small>}</span>
    </label>
  );
}

/* ---------- Layout pieces ---------- */
export function Modal({ title, onClose, children, footer, wide }: { title: ReactNode; onClose(): void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close.current(); };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('modal-open');
    return () => { document.removeEventListener('keydown', onKey); document.body.classList.remove('modal-open'); };
  }, []);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={cx('modal', wide && 'modal-wide')} role="dialog" aria-modal="true">
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Tabs({ tabs, value, onChange }: { tabs: { id: string; label: ReactNode; hidden?: boolean }[]; value: string; onChange(id: string): void }) {
  return (
    <div className="tabs" role="tablist">
      {tabs.filter((t) => !t.hidden).map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={value === t.id} className={cx('tab', value === t.id && 'active')} onClick={() => onChange(t.id)}>{t.label}</button>
      ))}
    </div>
  );
}

export const Card = ({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) => (
  <section className={cx('card', className)}>
    {(title || actions) && <div className="card-head">{title && <h3>{title}</h3>}{actions && <div className="row gap-sm">{actions}</div>}</div>}
    {children}
  </section>
);

export const PageHeader = ({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) => (
  <div className="page-head">
    <div><h1>{title}</h1>{subtitle && <p className="muted">{subtitle}</p>}</div>
    {actions && <div className="row gap-sm wrap">{actions}</div>}
  </div>
);

export const Spinner = () => <span className="spinner" role="progressbar" aria-label="Loading" />;
export const Loading = () => <div className="loading"><Spinner /></div>;
export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const i18n = useI18n();
  return (
    <div className="alert alert-error">
      <span>{errorText(error, i18n)}</span>
      {onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>{i18n.t('common.retry')}</Button>}
    </div>
  );
}
export const Empty = ({ children, icon = '○' }: { children: ReactNode; icon?: string }) => <div className="empty"><div className="empty-icon" aria-hidden="true">{icon}</div><div>{children}</div></div>;
export const Stat = ({ label, value, hint, tone }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: 'warn' | 'bad' | 'good' }) => (
  <div className={cx('stat', tone && `stat-${tone}`)}><div className="stat-label">{label}</div><div className="stat-value">{value}</div>{hint && <div className="stat-hint">{hint}</div>}</div>
);

const GOOD = new Set(['active', 'booked', 'attended', 'paid', 'completed', 'won', 'signed', 'confirmed', 'approved', 'checked_in', 'connected', 'done', 'sent', 'processed', 'succeeded', 'available', 'shipped', 'ready']);
const WARN = new Set(['pending', 'pending_payment', 'waitlisted', 'cancellation_pending', 'paused', 'trial', 'requested', 'viewed', 'draft', 'open', 'new', 'contacted', 'trial_booked', 'trial_attended', 'offer_sent', 'queued', 'onboarding', 'handoff', 'withdrawal_requested', 'partially_refunded', 'warning', 'lead', 'reminder_1', 'reminder_2']);
const BAD = new Set(['failed', 'no_show', 'suspended', 'cancelled', 'late_cancelled', 'lost', 'expired', 'withdrawn', 'rejected', 'disputed', 'blocked', 'critical', 'hold_expired', 'unavailable', 'uncollectible', 'formal_notice', 'collection', 'past_due']);
export function Badge({ value, label }: { value: string | null | undefined; label?: ReactNode }) {
  const i18n = useI18n();
  if (!value) return null;
  const tone = GOOD.has(value) ? 'good' : BAD.has(value) ? 'bad' : WARN.has(value) ? 'warn' : 'neutral';
  return <span className={`badge badge-${tone}`}>{label ?? i18n.option(value)}</span>;
}

export interface Column<T> { key: string; label: ReactNode; render?: (row: T) => ReactNode; className?: string }
export function DataTable<T extends Record<string, any>>({ rows, columns, onRowClick, empty, rowKey = 'id' }: { rows: T[]; columns: Column<T>[]; onRowClick?: (row: T) => void; empty?: ReactNode; rowKey?: string }) {
  const i18n = useI18n();
  if (!rows.length) return <Empty>{empty ?? i18n.t('common.nothing_here')}</Empty>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr>{columns.map((c) => <th key={c.key} className={c.className}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={String(r[rowKey] ?? i)} className={onRowClick ? 'clickable' : undefined} onClick={onRowClick ? () => onRowClick(r) : undefined}>
              {columns.map((c) => <td key={c.key} className={c.className} data-label={typeof c.label === 'string' ? c.label : undefined}>{c.render ? c.render(r) : String(r[c.key] ?? '—')}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage(p: number): void }) {
  const i18n = useI18n();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="pager">
      <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</Button>
      <span className="muted">{i18n.t('common.page_of', { page, pages, total })}</span>
      <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>›</Button>
    </div>
  );
}

/** Search-as-you-type person picker for staff screens. */
export function PersonPicker({ slug, value, onChange, placeholder }: { slug: string; value: { id: string; label: string } | null; onChange(v: { id: string; label: string } | null): void; placeholder?: string }) {
  const i18n = useI18n();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<{ id: string; label: string; sub?: string }[]>([]);
  const debounced = useDebounced(q, 250);
  useEffect(() => {
    if (!open) return;
    api<{ id: string; label: string; sub?: string }[]>(`/v1/t/${slug}/admin/options/persons`, { query: { q: debounced } }).then(setResults).catch(() => setResults([]));
  }, [debounced, open, slug]);
  if (value) {
    return (
      <div className="picked">
        <span>{value.label}</span>
        <button type="button" className="icon-btn" aria-label={i18n.t('common.clear')} onClick={() => onChange(null)}>×</button>
      </div>
    );
  }
  return (
    <div className="picker">
      <Input value={q} placeholder={placeholder ?? i18n.t('common.search_person')} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} />
      {open && results.length > 0 && (
        <div className="picker-list">
          {results.map((r) => (
            <button type="button" key={r.id} onClick={() => { onChange({ id: r.id, label: r.label }); setOpen(false); setQ(''); }}>
              <strong>{r.label}</strong>{r.sub && <small className="muted"> {r.sub}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CopyField({ value }: { value: string }) {
  const i18n = useI18n();
  const toast = useToast();
  return (
    <div className="copy-field">
      <input className="input mono" readOnly value={value} onFocus={(e) => e.target.select()} />
      <Button variant="secondary" size="sm" onClick={() => navigator.clipboard.writeText(value).then(() => toast.ok(i18n.t('common.copied')))}>{i18n.t('common.copy')}</Button>
    </div>
  );
}
